import type { SVGProps } from "react";

import archive from "disarto-icons-static/icons/regular/archive.svg?raw";
import alertCircle from "disarto-icons-static/icons/regular/alert-circle.svg?raw";
import alertTriangle from "disarto-icons-static/icons/regular/alert-triangle.svg?raw";
import alignLeft from "disarto-icons-static/icons/regular/align-left.svg?raw";
import arrowDown from "disarto-icons-static/icons/regular/arrow-down.svg?raw";
import arrowLeft from "disarto-icons-static/icons/regular/arrow-left.svg?raw";
import arrowUp from "disarto-icons-static/icons/regular/arrow-up.svg?raw";
import atom from "disarto-icons-static/icons/regular/atom.svg?raw";
import calendar from "disarto-icons-static/icons/regular/calendar.svg?raw";
import check from "disarto-icons-static/icons/regular/check.svg?raw";
import checkDouble from "disarto-icons-static/icons/regular/check-double.svg?raw";
import checkCircle from "disarto-icons-static/icons/regular/check-circle.svg?raw";
import chevronDown from "disarto-icons-static/icons/regular/chevron-down.svg?raw";
import chevronRight from "disarto-icons-static/icons/regular/chevron-right.svg?raw";
import chevronUp from "disarto-icons-static/icons/regular/chevron-up.svg?raw";
import circle from "disarto-icons-static/icons/regular/circle.svg?raw";
import clock from "disarto-icons-static/icons/regular/clock.svg?raw";
import close from "disarto-icons-static/icons/regular/close.svg?raw";
import code from "disarto-icons-static/icons/regular/code.svg?raw";
import copy from "disarto-icons-static/icons/regular/copy.svg?raw";
import cycle from "disarto-icons-static/icons/regular/cycle.svg?raw";
import cpu from "disarto-icons-static/icons/regular/cpu.svg?raw";
import envelope from "disarto-icons-static/icons/regular/envelope.svg?raw";
import expand from "disarto-icons-static/icons/regular/expand.svg?raw";
import file from "disarto-icons-static/icons/regular/file.svg?raw";
import gear from "disarto-icons-static/icons/regular/gear.svg?raw";
import homeSimple from "disarto-icons-static/icons/regular/home-simple.svg?raw";
import infoCircle from "disarto-icons-static/icons/regular/info-circle.svg?raw";
import link from "disarto-icons-static/icons/regular/link.svg?raw";
import lightning from "disarto-icons-static/icons/regular/lightning.svg?raw";
import minimize from "disarto-icons-static/icons/regular/minimize.svg?raw";
import moreHorizontal from "disarto-icons-static/icons/regular/more-horizontal.svg?raw";
import paperclip from "disarto-icons-static/icons/regular/paperclip.svg?raw";
import paperPlaneUp from "disarto-icons-static/icons/regular/paper-plane-up.svg?raw";
import panelLeft from "disarto-icons-static/icons/regular/panel-left.svg?raw";
import pin from "disarto-icons-static/icons/regular/pin.svg?raw";
import pinSolid from "disarto-icons-static/icons/solid/pin.svg?raw";
import plus from "disarto-icons-static/icons/regular/plus.svg?raw";
import plusCircle from "disarto-icons-static/icons/regular/plus-circle.svg?raw";
import pulse from "disarto-icons-static/icons/regular/pulse.svg?raw";
import questionCircle from "disarto-icons-static/icons/regular/question-circle.svg?raw";
import refresh from "disarto-icons-static/icons/regular/refresh.svg?raw";
import search from "disarto-icons-static/icons/regular/search.svg?raw";
import serverStack from "disarto-icons-static/icons/regular/server-stack.svg?raw";
import signOut from "disarto-icons-static/icons/regular/sign-out.svg?raw";
import squareSmall from "disarto-icons-static/icons/regular/square-small.svg?raw";
import toolbox from "disarto-icons-static/icons/regular/toolbox.svg?raw";
import trash from "disarto-icons-static/icons/regular/trash.svg?raw";

type IconProps = Omit<SVGProps<SVGSVGElement>, "height" | "name" | "width"> & {
  readonly isAnimated?: boolean;
  readonly size?: number | string;
};

const icons = {
  alignLeft,
  alertCircle,
  alertTriangle,
  archive,
  arrowDown,
  arrowLeft,
  arrowUp,
  atom,
  calendar,
  check,
  checkDouble,
  checkCircle,
  chevronDown,
  chevronRight,
  chevronUp,
  circle,
  clock,
  close,
  code,
  copy,
  cpu,
  cycle,
  envelope,
  expand,
  file,
  gear,
  homeSimple,
  infoCircle,
  link,
  lightning,
  minimize,
  moreHorizontal,
  paperclip,
  paperPlaneUp,
  panelLeft,
  pin,
  pinSolid,
  plus,
  plusCircle,
  pulse,
  questionCircle,
  refresh,
  search,
  serverStack,
  signOut,
  squareSmall,
  toolbox,
  trash,
} as const;

type DisartoIconName = keyof typeof icons;

function iconContents(source: string): string {
  const openingTagEnd = source.indexOf(">");
  const closingTagStart = source.lastIndexOf("</svg>");
  return source.slice(openingTagEnd + 1, closingTagStart);
}

export function DisartoIcon({ name, ...props }: { readonly name: DisartoIconName } & IconProps) {
  const { isAnimated: _isAnimated, size = 20, ...rest } = props;

  return (
    <svg
      aria-hidden="true"
      {...rest}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      dangerouslySetInnerHTML={{ __html: iconContents(icons[name]) }}
    />
  );
}

export const ArchiveIcon = (props: IconProps) => <DisartoIcon name="archive" {...props} />;
export const ArrowDownIcon = (props: IconProps) => <DisartoIcon name="arrowDown" {...props} />;
export const ArrowLeftIcon = (props: IconProps) => <DisartoIcon name="arrowLeft" {...props} />;
export const ArrowUpIcon = (props: IconProps) => <DisartoIcon name="arrowUp" {...props} />;
export const AlertIcon = (props: IconProps) => <DisartoIcon name="alertCircle" {...props} />;
export const ActivityIcon = (props: IconProps) => <DisartoIcon name="pulse" {...props} />;
export const BugIcon = (props: IconProps) => <DisartoIcon name="toolbox" {...props} />;
export const BoltIcon = (props: IconProps) => <DisartoIcon name="lightning" {...props} />;
export const BoxIcon = (props: IconProps) => <DisartoIcon name="serverStack" {...props} />;
export const CalendarIcon = (props: IconProps) => <DisartoIcon name="calendar" {...props} />;
export const CheckCircleIcon = (props: IconProps) => <DisartoIcon name="checkCircle" {...props} />;
export const CheckDoubleIcon = (props: IconProps) => <DisartoIcon name="checkDouble" {...props} />;
export const CheckIcon = (props: IconProps) => <DisartoIcon name="check" {...props} />;
export const ChevronDownIcon = (props: IconProps) => <DisartoIcon name="chevronDown" {...props} />;
export const ChevronRightIcon = (props: IconProps) => (
  <DisartoIcon name="chevronRight" {...props} />
);
export const ChevronUpIcon = (props: IconProps) => <DisartoIcon name="chevronUp" {...props} />;
export const ChipIcon = (props: IconProps) => <DisartoIcon name="cpu" {...props} />;
export const CycleIcon = (props: IconProps) => <DisartoIcon name="cycle" {...props} />;
export const ClockIcon = (props: IconProps) => <DisartoIcon name="clock" {...props} />;
export const CloseIcon = (props: IconProps) => <DisartoIcon name="close" {...props} />;
export const CodeIcon = (props: IconProps) => <DisartoIcon name="code" {...props} />;
export const CopyIcon = (props: IconProps) => <DisartoIcon name="copy" {...props} />;
export const DotsHorizontal = (props: IconProps) => (
  <DisartoIcon name="moreHorizontal" {...props} />
);
export const ExpandIcon = (props: IconProps) => <DisartoIcon name="expand" {...props} />;
export const FileIcon = (props: IconProps) => <DisartoIcon name="file" {...props} />;
export const GearIcon = (props: IconProps) => <DisartoIcon name="gear" {...props} />;
export const HomeIcon = (props: IconProps) => <DisartoIcon name="homeSimple" {...props} />;
export const InformationIcon = (props: IconProps) => <DisartoIcon name="infoCircle" {...props} />;
export const LinkIcon = (props: IconProps) => <DisartoIcon name="link" {...props} />;
export const LogOutIcon = (props: IconProps) => <DisartoIcon name="signOut" {...props} />;
export const MailIcon = (props: IconProps) => <DisartoIcon name="envelope" {...props} />;
export const MinimizeIcon = (props: IconProps) => <DisartoIcon name="minimize" {...props} />;
export const PaperclipIcon = (props: IconProps) => <DisartoIcon name="paperclip" {...props} />;
export const PanelLeftIcon = (props: IconProps) => <DisartoIcon name="panelLeft" {...props} />;
// Disarto's collapse-bar frame, with the directional arrow removed.
export function SidebarPanelIcon({ isAnimated: _isAnimated, size = 20, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        clipRule="evenodd"
        d="M2.75 6.5A3.75 3.75 0 0 1 6.5 2.75h11a3.75 3.75 0 0 1 3.75 3.75v11a3.75 3.75 0 0 1-3.75 3.75h-11a3.75 3.75 0 0 1-3.75-3.75zM6.5 4.25A2.25 2.25 0 0 0 4.25 6.5v11a2.25 2.25 0 0 0 2.25 2.25h1.75V4.25zm11 15.5H9.75V4.25h7.75a2.25 2.25 0 0 1 2.25 2.25v11a2.25 2.25 0 0 1-2.25 2.25"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  );
}
export const PlusCircleIcon = (props: IconProps) => <DisartoIcon name="plusCircle" {...props} />;
export const PlusIcon = (props: IconProps) => <DisartoIcon name="plus" {...props} />;
export const QuestionMarkCircleIcon = (props: IconProps) => (
  <DisartoIcon name="questionCircle" {...props} />
);
export const RecordIcon = (props: IconProps) => <DisartoIcon name="circle" {...props} />;
export const RefreshIcon = (props: IconProps) => <DisartoIcon name="refresh" {...props} />;
export const SearchIcon = (props: IconProps) => <DisartoIcon name="search" {...props} />;
export const SquareFilledIcon = (props: IconProps) => <DisartoIcon name="squareSmall" {...props} />;
export const StaticTrashIcon = (props: IconProps) => <DisartoIcon name="trash" {...props} />;
export const TextAlignLeftIcon = (props: IconProps) => <DisartoIcon name="alignLeft" {...props} />;
export const WarningTriangleIcon = (props: IconProps) => (
  <DisartoIcon name="alertTriangle" {...props} />
);
export const XIcon = (props: IconProps) => <DisartoIcon name="close" {...props} />;

export const ModelContextProtocolIcon = (props: IconProps) => (
  <DisartoIcon name="atom" {...props} />
);
export const PaperAirplaneIcon = (props: IconProps) => (
  <DisartoIcon name="paperPlaneUp" {...props} />
);
export const SendIcon = (props: IconProps) => <DisartoIcon name="paperPlaneUp" {...props} />;
export const TrashIcon = (props: IconProps) => <DisartoIcon name="trash" {...props} />;
export const PinTackIcon = (props: IconProps) => <DisartoIcon name="pin" {...props} />;
export const PinTackFilledIcon = (props: IconProps) => <DisartoIcon name="pinSolid" {...props} />;
